import { useBreakpoint } from 'components/BreakpointProvider';
import { useSimilarBoatsQuery } from 'components/pdp/v3/hooks';
import BoatCard from 'components/search/v2/BoatCard/BoatCard';
import { getBoatCardUrl } from 'components/search/v2/utils';
import { mediaMMdMax } from 'helpers/breakpoints';
import { Fragment } from 'react';
import styled from 'styled-components';
import { SimilarBoat } from 'swagger/models';
import { Flex } from '../../primitives/flex';
import { SubHeading } from './common';

const renderBoatCard = (boat, index) => (
  <BoatCard
    key={index}
    boat={boat}
    index={index}
    url={getBoatCardUrl({
      boatId: boat.boat_public_id,
      pos: index,
    })}
  />
);

export default function SimilarBoats({ boatId }: { boatId: string }) {
  const { data: similarBoatsData, isLoading } = useSimilarBoatsQuery(boatId);

  const breakpoints = useBreakpoint();
  const smallScreen = breakpoints.xs || breakpoints.sm || breakpoints.md;

  const similarBoats = (similarBoatsData?.data as { data: SimilarBoat[] } | undefined)?.data?.filter(
    (b) => b.boat_public_id !== boatId
  );

  if (similarBoats?.length === 0) return null;

  return (
    <div data-testid="pdp-similar-boats">
      {!isLoading && (
        <StyledSimilarBoats>
          <SubHeading>Similar boats</SubHeading>
          {!smallScreen ? (
            <Flex gap="25px">
              {similarBoats?.slice(0, 3)?.map((boat, index) => (
                <Fragment key={index}>{renderBoatCard(boat, index)}</Fragment>
              ))}
            </Flex>
          ) : (
            <ScrollContainer>
              {(similarBoats || []).map((boat, index) => (
                <Slide key={boat.boat_public_id} data-testid="pdp-similar-boats-boat-card">
                  <Flex>{renderBoatCard(boat, index)}</Flex>
                </Slide>
              ))}
            </ScrollContainer>
          )}
        </StyledSimilarBoats>
      )}
    </div>
  );
}

const StyledSimilarBoats = styled.div`
  padding-top: 60px;

  ${mediaMMdMax} {
    padding-top: 35px;
  }

  ${SubHeading} {
    margin-bottom: 22px;
  }
`;

const ScrollContainer = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  gap: 22px;
  margin-left: -10px;
  padding-left: 10px;
  scroll-padding-left: 10px;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Slide = styled.div`
  flex-shrink: 0;
  scroll-snap-align: start;
  width: calc((100% - 22px) / 1.25);
`;
